import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Cell,
  Tooltip, ResponsiveContainer, RadialBarChart, RadialBar, ComposedChart, Line, Legend, Area, ReferenceLine } from 'recharts';

interface WinningContent {
  caption: string;
  type: string;
  reach: number;
  likes: number;
  comments: number;
  saves: number;
  shares: number;
  permalink: string;
  date: string;
}

interface PostData {
  caption: string;
  type: string;
  media_product: string;
  date: string;
  reach: number;
  likes: number;
  comments: number;
  saves: number;
  shares: number;
  plays: number;
  engagement_score: number;
  engagement_rate: number;
  permalink: string;
}

interface AvailableMetric {
  id: string;
  name: string;
  category: string;
  color: string;
}

interface TimeseriesPoint {
  date: string;
  date_full: string;
  leads: number;
  spend: number;
  impressions: number;
  clicks: number;
  cpm: number;
  ctr: number;
  cpl: number;
  stories_count: number;
  stories_reach: number;
  organic_reach: number;
  organic_engagement: number;
  posts: number;
  engagement_rate: number;
  followers: number;
  follower_delta: number;
  account_reach: number;
  [key: string]: string | number;
}

interface WarRoomData {
  timestamp: string;
  ads: {
    cpm_global: number;
    ctr_global: number;
    spend_total_today: number;
    impressions_total: number;
    clicks_total: number;
    cpm_breakdown: Array<{
      account: string;
      cpm: number;
      spend: number;
      impressions: number;
    }>;
    cpm_avg_7d: number;
    cpm_delta_pct: number;
    cpm_alert: boolean;
  };
  leads: {
    count_today: number;
    count_7d: number;
    cpl_realtime: number;
    cpl_label: string;
    cpl_by_hour: Array<{
      hour: string;
      cpl: number;
      leads: number;
      spend: number;
    }>;
    cpl_scalable: boolean;
    correlation_data: Array<{
      date: string;
      leads: number;
      stories_views: number;
      stories_count: number;
      posts: number;
      post_caption: string;
    }>;
  };
  instagram: {
    stories_retention: number;
    stories_total_7d: number;
    organic_pulse: number;
    engagement_rate: number;
    follower_velocity: number;
    temperature: number;
    followers_count: number;
  };
  winning_content: WinningContent | null;
  all_posts: PostData[];
  type_metrics: {
    reels_teste: { count: number; avg_likes: number; avg_comments: number; avg_saves: number; avg_shares: number; avg_plays: number; avg_engagement_score: number; total_reach: number };
    reels: { count: number; avg_likes: number; avg_comments: number; avg_saves: number; avg_shares: number; avg_plays: number; avg_engagement_score: number; total_reach: number };
    carrossel: { count: number; avg_likes: number; avg_comments: number; avg_saves: number; avg_shares: number; avg_plays: number; avg_engagement_score: number; total_reach: number };
  };
  all_ads_table: Array<{
    ad_name: string;
    campaign: string;
    spend: number;
    reach: number;
    impressions: number;
    clicks: number;
    cpm: number;
    ctr: number;
    cpc: number;
    video_p50: number;
    active_days: number;
    first_date: string;
    last_date: string;
    preview_link: string;
    source: string;
  }>;
  alerts: {
    cpm_high: boolean;
    cpl_scalable: boolean;
  };
  timeseries_data: TimeseriesPoint[];
  available_metrics: AvailableMetric[];
}

function useInterval(callback: () => void, delayMs: number | null) {
  useEffect(() => {
    if (delayMs === null) return;
    const id = setInterval(callback, delayMs);
    return () => clearInterval(id);
  }, [callback, delayMs]);
}

/* Custom Tooltip for Correlation Chart */
const CorrelationTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || !payload.length) return null;
  const dataPoint = payload[0]?.payload;
  return (
    <div style={{
      background: '#1a1a2e', border: '1px solid #00ffcc', borderRadius: '8px',
      padding: '0.8rem 1rem', maxWidth: '280px', fontFamily: 'Courier New, monospace'
    }}>
      <div style={{ color: '#00ffcc', fontWeight: 'bold', marginBottom: '0.4rem', fontSize: '0.85rem' }}>
        📅 {label}
      </div>
      {payload.map((p: any, i: number) => (
        <div key={i} style={{ color: p.color || '#ccc', fontSize: '0.8rem', marginBottom: '0.2rem' }}>
          {p.name}: <strong>{p.value}</strong>
        </div>
      ))}
      {dataPoint?.post_caption && (
        <div style={{
          marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid #333',
          color: '#aaa', fontSize: '0.7rem', fontStyle: 'italic', lineHeight: '1.3'
        }}>
          📝 "{dataPoint.post_caption}"
        </div>
      )}
      {dataPoint?.stories_count > 0 && (
        <div style={{ color: '#3b82f6', fontSize: '0.7rem', marginTop: '0.3rem' }}>
          📱 {dataPoint.stories_count} stories postados
        </div>
      )}
    </div>
  );
};

interface FollowerHourlyPoint {
  hour: string;
  new_followers: number;
  end_time: string;
}

interface FollowersHourlyData {
  data: FollowerHourlyPoint[];
  granularity: string;
  total_new_followers: number;
  period_label: string;
}

const WarRoomDashboard: React.FC = () => {
  const [data, setData] = useState<WarRoomData | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>(['leads', 'spend']);
  const [timeRange, setTimeRange] = useState<number>(7);
  const [cardRange, setCardRange] = useState<number>(7);
  const [followersHourly, setFollowersHourly] = useState<FollowersHourlyData | null>(null);
  const [followersHourlyLoading, setFollowersHourlyLoading] = useState(false);
  const [postSort, setPostSort] = useState<{ field: string; dir: 'asc' | 'desc' }>({ field: 'date', dir: 'desc' });
  const [postExpanded, setPostExpanded] = useState<number | null>(null);
  const [igRefreshing, setIgRefreshing] = useState(false);
  const [igRefreshMsg, setIgRefreshMsg] = useState<string | null>(null);
  const [adSort, setAdSort] = useState<{ field: string; dir: 'asc' | 'desc' }>({ field: 'spend', dir: 'desc' });
  const [adDateFrom, setAdDateFrom] = useState<string>('');
  const [adDateTo, setAdDateTo] = useState<string>('');

  const toggleMetric = (metricId: string) => {
    setSelectedMetrics(prev => {
      if (prev.includes(metricId)) {
        return prev.filter(m => m !== metricId);
      }
      if (prev.length >= 4) return prev; // max 4 métricas simultâneas
      return [...prev, metricId];
    });
  };

  const fetchWarRoomData = useCallback(async () => {
    try {
      setError(null);
      const response = await fetch(`/api/api/v1/war-room/summary?days=${timeRange}`);
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      const json = await response.json();
      setData(json);
      setLastUpdate(new Date());
      setLoading(false);
    } catch (err: any) {
      console.error('War Room fetch error:', err);
      setError(err.message || 'Erro desconhecido');
      setLoading(false);
    }
  }, [timeRange]);

  const fetchFollowersHourly = useCallback(async () => {
    setFollowersHourlyLoading(true);
    try {
      const resp = await fetch('/api/api/v1/war-room/followers-hourly');
      if (resp.ok) {
        const json = await resp.json();
        setFollowersHourly(json);
      }
    } catch (e) {
      console.error('Followers hourly fetch error:', e);
    } finally {
      setFollowersHourlyLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWarRoomData();
  }, [fetchWarRoomData]);

  useEffect(() => {
    fetchFollowersHourly();
  }, [fetchFollowersHourly]);

  useInterval(fetchWarRoomData, 15 * 60 * 1000);
  useInterval(fetchFollowersHourly, 30 * 60 * 1000);

  if (loading && !data) {
    return (
      <div className="war-room-loading">
        <div className="cyber-spinner"></div>
        <p>INICIALIZANDO WAR ROOM...</p>
        <style>{`
          .war-room-loading {
            display: flex; flex-direction: column; justify-content: center;
            align-items: center; min-height: 100vh; background: #0a0a0a;
            color: #00ff41; font-family: 'Courier New', monospace;
          }
          .cyber-spinner {
            border: 4px solid #333; border-top: 4px solid #00ff41;
            border-radius: 50%; width: 60px; height: 60px;
            animation: spin 1s linear infinite; margin-bottom: 1rem;
          }
          @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
        alignItems: 'center', minHeight: '100vh', background: '#0a0a0a',
        color: '#00ff41', fontFamily: 'Courier New, monospace'
      }}>
        <h2>⚠️ ERRO NO SISTEMA</h2>
        <p>{error}</p>
        <button onClick={fetchWarRoomData} style={{
          background: '#00ff41', color: '#0a0a0a', border: 'none',
          padding: '0.8rem 2rem', fontSize: '1rem', fontWeight: 'bold',
          cursor: 'pointer', marginTop: '1rem', borderRadius: '4px'
        }}>TENTAR NOVAMENTE</button>
      </div>
    );
  }

  if (!data) return null;

  /* Temperature gauge: 0-100 scale */
  const temp = data.instagram.temperature;
  const tempColor = temp >= 60 ? '#00ff41' : temp >= 30 ? '#ffeb3b' : '#ff073a';
  const tempLabel = temp >= 60 ? 'CRESCIMENTO' : temp >= 30 ? 'ESTÁVEL' : 'DECLÍNIO';
  const tempGaugeData = [{ name: 'Temperature', value: temp, fill: tempColor }];

  /* CPL display */
  const cplValue = data.leads.cpl_realtime;
  const cplLabel = data.leads.cpl_label || 'hoje';

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', padding: '2rem', fontFamily: 'Courier New, monospace', color: '#e0e0e0' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '2.5rem', color: '#00ff41', letterSpacing: '4px', textTransform: 'uppercase' }}>
          ⚡ WAR ROOM: GROWTH & CPM
        </h1>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginTop: '0.5rem' }}>
          <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#00ff41', display: 'inline-block', animation: 'pulse 2s infinite' }}></span>
          <span style={{ color: '#888', fontSize: '0.9rem' }}>
            AO VIVO • Atualizado: {lastUpdate ? lastUpdate.toLocaleTimeString('pt-BR') : '—'}
          </span>
        </div>
      </div>

      {/* Metrics Grid — 5 cards: CPM, CTR, Gasto, CPL, Pulso Orgânico */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
        {/* CPM */}
        <div style={{ background: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '1.2rem' }}>
          <div style={{ color: '#888', fontSize: '0.75rem', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
            CPM {data.ads.ads_date_label === 'hoje' ? 'Global' : `(${data.ads.ads_date_label})`}
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#00ffcc' }}>R$ {data.ads.cpm_global.toFixed(2)}</div>
          <div style={{ color: '#555', fontSize: '0.7rem', marginTop: '0.3rem' }}>
            Custo por 1.000 impressões
          </div>
        </div>

        {/* CTR */}
        <div style={{ background: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '1.2rem' }}>
          <div style={{ color: '#888', fontSize: '0.75rem', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
            CTR {data.ads.ads_date_label === 'hoje' ? 'Global' : `(${data.ads.ads_date_label})`}
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#00ffcc' }}>
            {data.ads.ctr_global.toFixed(2)}%
          </div>
          <div style={{ color: '#666', fontSize: '0.8rem' }}>
            {(data.ads.clicks_total || 0).toLocaleString('pt-BR')} cliques / {((data.ads.impressions_total || 0) / 1000).toFixed(1)}k imp
          </div>
        </div>

        {/* Gasto */}
        <div style={{ background: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '1.2rem' }}>
          <div style={{ color: '#888', fontSize: '0.75rem', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
            Gasto {data.ads.ads_date_label === 'hoje' ? 'Total Hoje' : `(${data.ads.ads_date_label})`}
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#ff9500' }}>R$ {data.ads.spend_total_today.toFixed(2)}</div>
          <div style={{ color: '#666', fontSize: '0.8rem' }}>
            {(data.ads.impressions_total || 0).toLocaleString('pt-BR')} impressões
          </div>
        </div>

        {/* CPL */}
        <div style={{ background: '#1e1e1e', border: cplLabel !== 'hoje' ? '1px solid #ff9500' : '1px solid #333', borderRadius: '8px', padding: '1.2rem' }}>
          <div style={{ color: '#888', fontSize: '0.75rem', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
            CPL {cplLabel === 'hoje' ? 'Tempo Real' : `(${cplLabel})`}
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: cplValue > 0 ? '#ff00ff' : '#666' }}>
            {cplValue > 0 ? `R$ ${cplValue.toFixed(2)}` : '—'}
          </div>
          <div style={{ color: '#666', fontSize: '0.8rem' }}>
            {data.leads.count_today} leads hoje · {data.leads.count_7d} em 7d
          </div>
          {cplLabel !== 'hoje' && cplLabel !== 'sem dados' && (
            <div style={{ color: '#ff9500', fontSize: '0.65rem', marginTop: '0.3rem' }}>
              ⚠ Usando {cplLabel} (0 leads hoje)
            </div>
          )}
        </div>

        {/* Pulso Orgânico */}
        <div style={{ background: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '1.2rem' }}>
          <div style={{ color: '#888', fontSize: '0.75rem', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Pulso Orgânico</div>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#00ff41' }}>
            {(data.instagram.organic_pulse || 0).toLocaleString('pt-BR')}
          </div>
          <div style={{ color: '#666', fontSize: '0.8rem' }}>
            engajamento {data.instagram.engagement_rate || 0}%
          </div>
        </div>
      </div>

      {/* ── KPI CARDS: LEADS / SEGUIDORES / STORIES / POSTS ── */}
      {data.timeseries_data && data.timeseries_data.length > 0 && (() => {
        const ts = data.timeseries_data || [];
        const sliced = ts.slice(-cardRange);
        const totalLeads = sliced.reduce((s: number, p: any) => s + (p.leads || 0), 0);
        const totalStories = sliced.reduce((s: number, p: any) => s + (p.stories_count || 0), 0);
        const totalPosts = sliced.reduce((s: number, p: any) => s + (p.posts || 0), 0);
        const realFollowers = sliced.filter((p: any) => p.followers && p.followers > 0);
        const latestFollowers = realFollowers.length > 0 ? realFollowers[realFollowers.length - 1].followers : 0;
        const firstFollowers = realFollowers.length > 0 ? realFollowers[0].followers : 0;
        const followerGrowth = latestFollowers - firstFollowers;

        const kpiCards = [
          { label: 'Leads', value: totalLeads.toLocaleString('pt-BR'), sub: `${(totalLeads / cardRange).toFixed(1)}/dia`, color: '#ff00ff', icon: '🎯' },
          { label: 'Seguidores', value: latestFollowers.toLocaleString('pt-BR'), sub: `${followerGrowth >= 0 ? '+' : ''}${followerGrowth.toLocaleString('pt-BR')} no período`, color: '#8b5cf6', icon: '👥' },
          { label: 'Stories', value: totalStories.toLocaleString('pt-BR'), sub: `${(totalStories / cardRange).toFixed(1)}/dia`, color: '#3b82f6', icon: '📸' },
          { label: 'Posts/Reels', value: totalPosts.toLocaleString('pt-BR'), sub: `${(totalPosts / cardRange).toFixed(1)}/dia`, color: '#ff4444', icon: '📹' },
        ];

        return (
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <h3 style={{ color: '#00ffcc', margin: 0, textTransform: 'uppercase', fontSize: '0.85rem', letterSpacing: '2px' }}>
                📊 Resumo do Período
              </h3>
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                {([7, 14, 30, 90] as const).map((d) => (
                  <button
                    key={`cr-${d}`}
                    onClick={() => {
                      setCardRange(d);
                      if (d > timeRange) setTimeRange(d);
                    }}
                    style={{
                      background: cardRange === d ? '#00ffcc22' : '#0d0d0d',
                      border: `1px solid ${cardRange === d ? '#00ffcc' : '#444'}`,
                      color: cardRange === d ? '#00ffcc' : '#888',
                      borderRadius: '4px',
                      padding: '0.2rem 0.55rem',
                      fontSize: '0.7rem',
                      cursor: 'pointer',
                      fontFamily: 'monospace',
                      transition: 'all 0.15s',
                    }}
                  >
                    {d}d
                  </button>
                ))}
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
              {kpiCards.map((card) => (
                <div key={card.label} style={{
                  background: '#1e1e1e', border: `1px solid ${card.color}33`, borderRadius: '8px',
                  padding: '1.2rem', textAlign: 'center',
                  borderTop: `3px solid ${card.color}`
                }}>
                  <div style={{ fontSize: '1.4rem', marginBottom: '0.3rem' }}>{card.icon}</div>
                  <div style={{ color: '#888', fontSize: '0.7rem', textTransform: 'uppercase', marginBottom: '0.4rem', letterSpacing: '1px' }}>{card.label}</div>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', color: card.color }}>{card.value}</div>
                  <div style={{ color: '#666', fontSize: '0.75rem', marginTop: '0.3rem' }}>{card.sub}</div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Charts Row 1 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
        

        {/* Correlação */}
        <div style={{ background: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '1.5rem' }}>
          <h3 style={{ color: '#00ffcc', marginBottom: '1rem', textTransform: 'uppercase', fontSize: '0.9rem', letterSpacing: '2px' }}>
            📈 Correlação: Leads × Stories × Posts (7d)
          </h3>
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={data.leads.correlation_data || []}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis dataKey="date" stroke="#888" />
              <YAxis yAxisId="left" stroke="#ff00ff" allowDecimals={false} />
              <YAxis yAxisId="right" orientation="right" stroke="#3b82f6" allowDecimals={false} />
              <Tooltip content={<CorrelationTooltip />} />
              <Legend />
              {/* Marcadores de domingo */}
              {(() => {
                const nowM = new Date().getMonth() + 1;
                const nowY = new Date().getFullYear();
                return (data.leads.correlation_data || []).map((p: any) => p.date).filter((dateStr: string) => {
                  const parts = dateStr.split('/');
                  const dd = parseInt(parts[0]);
                  const mm = parseInt(parts[1]);
                  const year = mm > nowM ? nowY - 1 : nowY;
                  return new Date(year, mm - 1, dd).getDay() === 0;
                }).map((d: string) => (
                  <ReferenceLine
                    key={`sun-corr-${d}`}
                    x={d}
                    yAxisId="left"
                    stroke="#ffffff"
                    strokeOpacity={0.12}
                    strokeWidth={16}
                    label={{ value: 'Dom', position: 'insideTop', offset: 6, fontSize: 9, fill: '#777', fontFamily: 'monospace' }}
                  />
                ));
              })()}
              <Bar yAxisId="right" dataKey="stories_count" name="Stories Postados" barSize={22} fill="#3b82f6" opacity={0.7} radius={[4, 4, 0, 0]} />
              <Bar yAxisId="right" dataKey="posts" name="Posts/Reels" barSize={18} fill="#ff4444" opacity={0.6} radius={[4, 4, 0, 0]} />
              <Line yAxisId="left" type="monotone" dataKey="leads" name="Leads" stroke="#ff00ff" strokeWidth={3} dot={{ stroke: '#ff00ff', strokeWidth: 2, r: 5, fill: '#1e1e1e' }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── MULTI-METRIC CHART ────────────────────────── */}
      {data.timeseries_data && data.timeseries_data.length > 0 && (
        <div style={{ background: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '1.5rem', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '0.5rem' }}>
            <h3 style={{ color: '#00ffcc', margin: 0, textTransform: 'uppercase', fontSize: '0.9rem', letterSpacing: '2px' }}>
              📊 Análise Multi-Métrica ({timeRange} Dias)
            </h3>
            <div style={{ display: 'flex', gap: '0.4rem' }}>
              {([7, 14, 30, 90] as const).map((d) => (
                <button
                  key={d}
                  onClick={() => setTimeRange(d)}
                  style={{
                    background: timeRange === d ? '#00ffcc22' : '#0d0d0d',
                    border: `1px solid ${timeRange === d ? '#00ffcc' : '#444'}`,
                    color: timeRange === d ? '#00ffcc' : '#888',
                    borderRadius: '4px',
                    padding: '0.25rem 0.65rem',
                    fontSize: '0.72rem',
                    cursor: 'pointer',
                    fontFamily: 'monospace',
                    transition: 'all 0.15s',
                  }}
                >
                  {d}d
                </button>
              ))}
            </div>
          </div>
          <div style={{ color: '#666', fontSize: '0.7rem', marginBottom: '1rem' }}>
            Selecione até 4 métricas para comparar · Eixo esquerdo: 1ª métrica · Eixo direito: 2ª métrica
          </div>

          {/* Metric selector */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1.2rem' }}>
            {(data.available_metrics || []).map((m) => {
              const isSelected = selectedMetrics.includes(m.id);
              return (
                <button
                  key={m.id}
                  onClick={() => toggleMetric(m.id)}
                  style={{
                    background: isSelected ? m.color + '22' : '#0d0d0d',
                    border: `1px solid ${isSelected ? m.color : '#333'}`,
                    color: isSelected ? m.color : '#666',
                    borderRadius: '4px',
                    padding: '0.3rem 0.7rem',
                    fontSize: '0.72rem',
                    cursor: 'pointer',
                    fontFamily: 'Courier New, monospace',
                    transition: 'all 0.2s',
                  }}
                >
                  {isSelected ? '● ' : '○ '}{m.name}
                </button>
              );
            })}
          </div>

          {/* Multi-axis chart */}
          {selectedMetrics.length > 0 && (() => {
            const metricsMap = Object.fromEntries((data.available_metrics || []).map(m => [m.id, m]));
            const leftMetric = metricsMap[selectedMetrics[0]];
            const rightMetric = selectedMetrics.length > 1 ? metricsMap[selectedMetrics[1]] : null;

            const monthNamesShort = ['', 'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
            const monthNamesFull = ['', 'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

            const xTickFormatter = (value: string) => {
              const parts = value.split('/');
              const dd = parseInt(parts[0]);
              const mm = parseInt(parts[1]);
              if (timeRange >= 30 && dd === 1) {
                return `${monthNamesShort[mm]} 1`;
              }
              return `${dd}/${parts[1]}`;
            };

            const xInterval = timeRange >= 90 ? 6 : timeRange >= 30 ? 2 : 0;

            // Detectar domingos no timeseries
            const currentYear = new Date().getFullYear();
            const currentMonth = new Date().getMonth() + 1;
            const sundayDates = (data.timeseries_data || []).map(p => p.date).filter((dateStr: string) => {
              const parts = dateStr.split('/');
              const dd = parseInt(parts[0]);
              const mm = parseInt(parts[1]);
              const year = mm > currentMonth ? currentYear - 1 : currentYear;
              return new Date(year, mm - 1, dd).getDay() === 0;
            });

            return (
              <ResponsiveContainer width="100%" height={320}>
                <ComposedChart data={data.timeseries_data}>
                  <CartesianGrid stroke="#333" strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    stroke="#888"
                    tick={{ fontSize: 11, fill: '#888' }}
                    tickFormatter={xTickFormatter}
                    interval={xInterval}
                  />
                  <YAxis
                    yAxisId="left"
                    stroke={leftMetric?.color || '#fff'}
                    tick={{ fontSize: 11 }}
                    label={{ value: leftMetric?.name || '', angle: -90, position: 'insideLeft', style: { fill: leftMetric?.color || '#fff', fontSize: 10 } }}
                  />
                  {rightMetric && (
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      stroke={rightMetric.color}
                      tick={{ fontSize: 11 }}
                      label={{ value: rightMetric.name, angle: 90, position: 'insideRight', style: { fill: rightMetric.color, fontSize: 10 } }}
                    />
                  )}
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1a1a2e', border: '1px solid #00ffcc',
                      borderRadius: '8px', fontFamily: 'Courier New, monospace', fontSize: '0.8rem'
                    }}
                    labelFormatter={(label: string) => {
                      const parts = label.split('/');
                      const dd = parseInt(parts[0]);
                      const mm = parseInt(parts[1]);
                      return `📅 ${dd} de ${monthNamesFull[mm]}`;
                    }}
                    formatter={(value: number, name: string) => {
                      const m = (data.available_metrics || []).find(am => am.id === name);
                      const label = m?.name || name;
                      if (name === 'spend' || name === 'cpm' || name === 'cpl') return [`R$ ${value.toFixed(2)}`, label];
                      if (name === 'ctr' || name === 'engagement_rate') return [`${value.toFixed(2)}%`, label];
                      return [value.toLocaleString('pt-BR'), label];
                    }}
                  />
                  <Legend />
                  {/* Marcadores de domingo */}
                  {sundayDates.map((d: string) => (
                    <ReferenceLine
                      key={`sun-${d}`}
                      x={d}
                      yAxisId="left"
                      stroke="#ffffff"
                      strokeOpacity={0.18}
                      strokeWidth={16}
                      label={{ value: 'Dom', position: 'insideTop', offset: 6, fontSize: 9, fill: '#777', fontFamily: 'monospace' }}
                    />
                  ))}
                  {selectedMetrics.map((metricId, idx) => {
                    const m = metricsMap[metricId];
                    if (!m) return null;
                    const yId = idx === 0 ? 'left' : (idx === 1 && rightMetric ? 'right' : 'left');
                    if (idx < 2) {
                      return (
                        <Line
                          key={metricId}
                          yAxisId={yId}
                          type="monotone"
                          dataKey={metricId}
                          name={metricId}
                          stroke={m.color}
                          strokeWidth={2.5}
                          connectNulls={true}
                          dot={{ stroke: m.color, strokeWidth: 2, r: 4, fill: '#1e1e1e' }}
                          activeDot={{ r: 6 }}
                        />
                      );
                    }
                    return (
                      <Area
                        key={metricId}
                        yAxisId="left"
                        type="monotone"
                        dataKey={metricId}
                        name={metricId}
                        stroke={m.color}
                        fill={m.color}
                        fillOpacity={0.12}
                        strokeWidth={1.5}
                        connectNulls={true}
                      />
                    );
                  })}
                </ComposedChart>
              </ResponsiveContainer>
            );
          })()}
        </div>
      )}

      {/* Charts Row 2 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem', marginBottom: '2rem' }}>
        {/* Temperatura Instagram */}
        <div style={{ background: '#1e1e1e', border: `1px solid ${tempColor}44`, borderRadius: '8px', padding: '1.5rem' }}>
          <h3 style={{ color: '#00ffcc', marginBottom: '0.5rem', textTransform: 'uppercase', fontSize: '0.9rem', letterSpacing: '2px' }}>
            🔥 Temperatura Instagram
          </h3>
          <div style={{ color: '#666', fontSize: '0.7rem', marginBottom: '0.5rem' }}>
            Escala 0-100 · Verde=Crescimento · Vermelho=Declínio
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <RadialBarChart cx="50%" cy="70%" innerRadius="60%" outerRadius="90%" data={tempGaugeData} startAngle={180} endAngle={0}>
              <RadialBar
                dataKey="value"
                cornerRadius={10}
                fill={tempColor}
                background={{ fill: '#1a1a1a' }}
              />
            </RadialBarChart>
          </ResponsiveContainer>
          <div style={{ textAlign: 'center', marginTop: '-1.5rem' }}>
            <span style={{ color: tempColor, fontSize: '2.2rem', fontWeight: 'bold' }}>{temp}</span>
            <span style={{ color: '#666', fontSize: '0.9rem' }}>/100</span>
          </div>
          <div style={{ textAlign: 'center', color: tempColor, fontSize: '0.8rem', fontWeight: 'bold', letterSpacing: '2px', marginTop: '0.3rem' }}>
            {tempLabel}
          </div>
        </div>

        {/* CPM por Conta */}
        <div style={{ background: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '1.5rem' }}>
          <h3 style={{ color: '#00ffcc', marginBottom: '1rem', textTransform: 'uppercase', fontSize: '0.9rem', letterSpacing: '2px' }}>
            💰 CPM por Conta
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={data.ads.cpm_breakdown}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis dataKey="account" stroke="#888" tick={{ fontSize: 8 }} />
              <YAxis stroke="#888" />
              <Tooltip contentStyle={{ backgroundColor: '#1e1e1e', border: '1px solid #333', fontFamily: 'Courier New' }} />
              <Bar dataKey="cpm" fill="#00ffcc" name="CPM (R$)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Leads por Hora */}
        <div style={{ background: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '1.5rem' }}>
          <h3 style={{ color: '#00ffcc', marginBottom: '1rem', textTransform: 'uppercase', fontSize: '0.9rem', letterSpacing: '2px' }}>
            📊 Leads por Hora (Hoje - Horário Brasília)
          </h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.leads.cpl_by_hour}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis dataKey="hour" stroke="#888" tick={{ fontSize: 9 }} interval={2} />
              <YAxis stroke="#888" allowDecimals={false} />
              <Tooltip contentStyle={{ backgroundColor: '#1e1e1e', border: '1px solid #333', fontFamily: 'Courier New' }} />
              <Bar dataKey="leads" fill="#ff00ff" name="Leads" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        
      </div>

      {/* 👥 Novos Seguidores por Hora */}
      <div style={{ background: '#1e1e1e', border: '1px solid #8b5cf633', borderTop: '3px solid #8b5cf6', borderRadius: '8px', padding: '1.5rem', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
          <div>
            <h3 style={{ color: '#8b5cf6', margin: 0, textTransform: 'uppercase', fontSize: '0.9rem', letterSpacing: '2px' }}>
              👥 Novos Seguidores — {followersHourly?.period_label || 'últimas 24h'}
            </h3>
            {followersHourly && (
              <div style={{ color: '#666', fontSize: '0.72rem', marginTop: '0.3rem' }}>
                Total no período: <span style={{ color: '#8b5cf6', fontWeight: 'bold' }}>+{followersHourly.total_new_followers.toLocaleString('pt-BR')}</span>
                {followersHourly.granularity === 'day' || followersHourly.granularity === 'day_db'
                  ? ' · dados diários (API não retornou granularidade horária)'
                  : ' · granularidade horária · Brasília'}
              </div>
            )}
          </div>
          <button
            disabled={followersHourlyLoading}
            onClick={fetchFollowersHourly}
            style={{
              background: followersHourlyLoading ? '#333' : '#1a1a2e',
              border: '1px solid #8b5cf6',
              borderRadius: '6px',
              color: '#8b5cf6',
              padding: '0.4rem 1rem',
              fontSize: '0.75rem',
              cursor: followersHourlyLoading ? 'wait' : 'pointer',
              letterSpacing: '1px',
            }}
          >
            {followersHourlyLoading ? '⏳ Buscando...' : '🔄 Atualizar'}
          </button>
        </div>

        {followersHourlyLoading && !followersHourly && (
          <div style={{ color: '#666', fontSize: '0.85rem', textAlign: 'center', padding: '2rem' }}>
            Buscando dados do Instagram...
          </div>
        )}

        {followersHourly && followersHourly.data.length > 0 && (() => {
          const maxVal = Math.max(...followersHourly.data.map(d => d.new_followers), 1);
          const peakPoint = followersHourly.data.reduce((a, b) => a.new_followers >= b.new_followers ? a : b);
          return (
            <div>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={followersHourly.data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid stroke="#333" strokeDasharray="3 3" />
                  <XAxis
                    dataKey="hour"
                    stroke="#888"
                    tick={{ fontSize: followersHourly.granularity === 'hour' ? 9 : 11 }}
                    interval={followersHourly.granularity === 'hour' ? 2 : 0}
                  />
                  <YAxis stroke="#888" allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #8b5cf6', borderRadius: '8px', fontFamily: 'Courier New, monospace', fontSize: '0.8rem' }}
                    formatter={(value: number) => [`+${value} seguidores`, 'Novos seguidores']}
                    labelFormatter={(label: string) => `🕐 ${label}`}
                  />
                  <Bar dataKey="new_followers" name="Novos seguidores" radius={[4, 4, 0, 0]}>
                    {followersHourly.data.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.new_followers === maxVal && maxVal > 0 ? '#a78bfa' : '#8b5cf6'}
                        fillOpacity={entry.new_followers === 0 ? 0.2 : 0.85}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              {maxVal > 0 && (
                <div style={{ marginTop: '0.75rem', display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
                  <div style={{ fontSize: '0.8rem' }}>
                    <span style={{ color: '#888' }}>Pico: </span>
                    <span style={{ color: '#a78bfa', fontWeight: 'bold' }}>{peakPoint.hour}</span>
                    <span style={{ color: '#666' }}> (+{peakPoint.new_followers})</span>
                  </div>
                  <div style={{ fontSize: '0.8rem' }}>
                    <span style={{ color: '#888' }}>Média por {followersHourly.granularity === 'hour' ? 'hora' : 'dia'}: </span>
                    <span style={{ color: '#8b5cf6', fontWeight: 'bold' }}>
                      +{(followersHourly.total_new_followers / Math.max(followersHourly.data.length, 1)).toFixed(1)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {followersHourly && followersHourly.data.length === 0 && !followersHourlyLoading && (
          <div style={{ color: '#666', fontSize: '0.85rem', textAlign: 'center', padding: '2rem' }}>
            Sem dados de seguidores disponíveis
          </div>
        )}
      </div>

      

      {/* 🧪 Métricas por Tipo de Conteúdo */}
      {data.type_metrics && (
        <div style={{ background: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '1.5rem', marginBottom: '2rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <h3 style={{ color: '#00ffcc', margin: 0, textTransform: 'uppercase', fontSize: '0.9rem', letterSpacing: '2px' }}>
              🧪 Métricas por Tipo de Conteúdo
            </h3>
            <button
              disabled={igRefreshing}
              onClick={async () => {
                setIgRefreshing(true);
                setIgRefreshMsg(null);
                try {
                  const r = await fetch('/api/api/v1/war-room/refresh-ig', { method: 'POST' });
                  const j = await r.json();
                  setIgRefreshMsg(j.message || 'Atualizado');
                  fetchWarRoomData();
                } catch (e: any) {
                  setIgRefreshMsg('Erro: ' + (e.message || 'falha'));
                } finally {
                  setIgRefreshing(false);
                  setTimeout(() => setIgRefreshMsg(null), 6000);
                }
              }}
              style={{ background: igRefreshing ? '#333' : '#1a3a2a', border: '1px solid #00ff41', borderRadius: '6px', color: '#00ff41', padding: '0.4rem 1rem', fontSize: '0.75rem', cursor: igRefreshing ? 'wait' : 'pointer', letterSpacing: '1px' }}
            >
              {igRefreshing ? '⏳ Atualizando...' : '🔄 Refresh Instagram'}
            </button>
          </div>
          {igRefreshMsg && <div style={{ color: '#ffeb3b', fontSize: '0.75rem', marginBottom: '0.75rem' }}>{igRefreshMsg}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
            {[
              { key: 'reels' as const, label: '🎬 Reels', color: '#ff4444', border: '#ff4444' },
              { key: 'reels_teste' as const, label: '🧪 Reels Teste', color: '#ff9500', border: '#ff9500' },
              { key: 'carrossel' as const, label: '📑 Carrossel', color: '#00bfff', border: '#00bfff' },
            ].map(({ key, label, color, border }) => {
              const m = data.type_metrics[key];
              if (!m || m.count === 0) return (
                <div key={key} style={{ background: '#171717', borderRadius: '8px', padding: '1rem', border: `1px solid #333`, opacity: 0.5 }}>
                  <div style={{ color: '#666', fontSize: '0.85rem', marginBottom: '0.5rem' }}>{label}</div>
                  <div style={{ color: '#555', fontSize: '0.75rem' }}>Sem dados</div>
                </div>
              );
              return (
                <div key={key} style={{ background: '#171717', borderRadius: '8px', padding: '1rem', borderLeft: `3px solid ${border}`, border: `1px solid #333`, borderLeftColor: border }}>
                  <div style={{ color, fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '0.75rem' }}>{label} <span style={{ color: '#888', fontWeight: 'normal' }}>({m.count})</span></div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.4rem', fontSize: '0.75rem' }}>
                    <div>❤️ Likes: <span style={{ color: '#fff', fontWeight: 'bold' }}>{m.avg_likes}</span></div>
                    <div>💬 Coment: <span style={{ color: '#fff', fontWeight: 'bold' }}>{m.avg_comments}</span></div>
                    <div>🔖 Salvos: <span style={{ color: '#fff', fontWeight: 'bold' }}>{m.avg_saves}</span></div>
                    <div>🔁 Shares: <span style={{ color: '#fff', fontWeight: 'bold' }}>{m.avg_shares}</span></div>
                    <div>▶️ Plays: <span style={{ color: '#fff', fontWeight: 'bold' }}>{m.avg_plays}</span></div>
                    <div>⚡ Score: <span style={{ color: '#fff', fontWeight: 'bold' }}>{m.avg_engagement_score}</span></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 📊 Todas as Postagens — Performance Completa */}
      <div style={{ background: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '1.5rem', marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <h3 style={{ color: '#00ffcc', margin: 0, textTransform: 'uppercase', fontSize: '0.9rem', letterSpacing: '2px' }}>
            📊 Todas as Postagens — Performance
          </h3>
          <span style={{ color: '#666', fontSize: '0.7rem' }}>
            {(data.all_posts || []).length} posts · clique no cabeçalho para ordenar
          </span>
        </div>

        {(data.all_posts || []).length > 0 ? (() => {
          const sortedPosts = [...(data.all_posts || [])].sort((a: any, b: any) => {
            const av = a[postSort.field];
            const bv = b[postSort.field];
            if (typeof av === 'string') return postSort.dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
            return postSort.dir === 'asc' ? av - bv : bv - av;
          });

          const maxReach = Math.max(...sortedPosts.map((p: PostData) => p.reach), 1);
          const maxScore = Math.max(...sortedPosts.map((p: PostData) => p.engagement_score), 1);

          const headerStyle = (field: string) => ({
            color: postSort.field === field ? '#00ffcc' : '#888',
            fontSize: '0.65rem',
            textTransform: 'uppercase' as const,
            cursor: 'pointer',
            userSelect: 'none' as const,
            letterSpacing: '0.5px',
            whiteSpace: 'nowrap' as const,
            padding: '0.6rem 0.4rem',
          });

          const handleSort = (field: string) => {
            setPostSort(prev => ({
              field,
              dir: prev.field === field && prev.dir === 'desc' ? 'asc' : 'desc'
            }));
          };

          const sortArrow = (field: string) => postSort.field === field ? (postSort.dir === 'desc' ? ' ▼' : ' ▲') : '';

          return (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #333' }}>
                    <th style={{ ...headerStyle('date'), textAlign: 'left', minWidth: '70px' }} onClick={() => handleSort('date')}>Data{sortArrow('date')}</th>
                    <th style={{ ...headerStyle('type'), textAlign: 'left', minWidth: '60px' }} onClick={() => handleSort('type')}>Tipo{sortArrow('type')}</th>
                    <th style={{ ...headerStyle('caption'), textAlign: 'left', minWidth: '200px' }} onClick={() => handleSort('caption')}>Conteúdo{sortArrow('caption')}</th>
                    <th style={{ ...headerStyle('reach'), textAlign: 'right' }} onClick={() => handleSort('reach')}>Alcance{sortArrow('reach')}</th>
                    <th style={{ ...headerStyle('likes'), textAlign: 'right' }} onClick={() => handleSort('likes')}>Likes{sortArrow('likes')}</th>
                    <th style={{ ...headerStyle('comments'), textAlign: 'right' }} onClick={() => handleSort('comments')}>Com.{sortArrow('comments')}</th>
                    <th style={{ ...headerStyle('saves'), textAlign: 'right' }} onClick={() => handleSort('saves')}>Saves{sortArrow('saves')}</th>
                    <th style={{ ...headerStyle('shares'), textAlign: 'right' }} onClick={() => handleSort('shares')}>Shares{sortArrow('shares')}</th>
                    <th style={{ ...headerStyle('plays'), textAlign: 'right' }} onClick={() => handleSort('plays')}>Plays{sortArrow('plays')}</th>
                    <th style={{ ...headerStyle('engagement_rate'), textAlign: 'right' }} onClick={() => handleSort('engagement_rate')}>Eng%{sortArrow('engagement_rate')}</th>
                    <th style={{ ...headerStyle('engagement_score'), textAlign: 'right', minWidth: '60px' }} onClick={() => handleSort('engagement_score')}>Score{sortArrow('engagement_score')}</th>
                    <th style={{ ...headerStyle('permalink'), textAlign: 'center', minWidth: '30px' }}>🔗</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedPosts.map((post: PostData, idx: number) => {
                    const reachPct = post.reach / maxReach;
                    const scorePct = post.engagement_score / maxScore;
                    const isTop = scorePct >= 0.8;
                    const isBad = scorePct <= 0.2 && sortedPosts.length > 3;
                    const rowBg = isTop ? '#1a2e1a' : isBad ? '#2e1a1a' : (idx % 2 === 0 ? '#1e1e1e' : '#171717');
                    const isExpanded = postExpanded === idx;

                    const typeIcon = post.type === 'Reels Teste' ? '🧪' : post.media_product === 'REELS' || post.type === 'Reels' ? '🎬' : post.type?.includes('Vídeo') || post.type?.includes('VIDEO') ? '📹' : post.type?.includes('Carrossel') || post.type?.includes('CAROUSEL') ? '📑' : '📷';
                    const typeLabel = post.type === 'Reels Teste' ? 'Reels Teste' : post.media_product === 'REELS' || post.type === 'Reels' ? 'Reel' : post.type?.includes('Vídeo') || post.type?.includes('VIDEO') ? 'Vídeo' : post.type?.includes('Carrossel') || post.type?.includes('CAROUSEL') ? 'Carousel' : 'Imagem';

                    // Color intensity based on performance
                    const metricColor = (val: number, max: number) => {
                      if (max === 0) return '#555';
                      const ratio = val / max;
                      if (ratio >= 0.7) return '#00ff41';
                      if (ratio >= 0.4) return '#ffeb3b';
                      if (ratio >= 0.15) return '#ff9500';
                      return '#666';
                    };
                    const allLikes = sortedPosts.map(p => p.likes);
                    const allComments = sortedPosts.map(p => p.comments);
                    const allSaves = sortedPosts.map(p => p.saves);
                    const allShares = sortedPosts.map(p => p.shares);
                    const maxL = Math.max(...allLikes, 1);
                    const maxC = Math.max(...allComments, 1);
                    const maxSv = Math.max(...allSaves, 1);
                    const maxSh = Math.max(...allShares, 1);

                    return (
                      <React.Fragment key={idx}>
                        <tr
                          style={{ borderBottom: '1px solid #222', background: rowBg, cursor: 'pointer', transition: 'background 0.15s' }}
                          onClick={() => setPostExpanded(isExpanded ? null : idx)}
                          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = '#252535'; }}
                          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = rowBg; }}
                        >
                          <td style={{ padding: '0.6rem 0.4rem', color: '#aaa', fontSize: '0.72rem', whiteSpace: 'nowrap' }}>
                            {post.date ? `${post.date.slice(8,10)}/${post.date.slice(5,7)}` : '—'}
                          </td>
                          <td style={{ padding: '0.6rem 0.4rem', whiteSpace: 'nowrap' }}>
                            <span title={typeLabel}>{typeIcon} {typeLabel}</span>
                          </td>
                          <td style={{ padding: '0.6rem 0.4rem', color: '#ccc', maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {isTop && <span style={{ color: '#ffd700', marginRight: '4px' }}>🏆</span>}
                            {post.caption || '(sem legenda)'}
                          </td>
                          <td style={{ padding: '0.6rem 0.4rem', textAlign: 'right', fontWeight: 'bold' }}>
                            <div style={{ position: 'relative' }}>
                              <div style={{
                                position: 'absolute', left: 0, top: 0, bottom: 0,
                                width: `${reachPct * 100}%`, background: '#ff444418',
                                borderRadius: '2px'
                              }} />
                              <span style={{ position: 'relative', color: metricColor(post.reach, maxReach) }}>
                                {post.reach.toLocaleString('pt-BR')}
                              </span>
                            </div>
                          </td>
                          <td style={{ padding: '0.6rem 0.4rem', textAlign: 'right', color: metricColor(post.likes, maxL) }}>
                            {post.likes.toLocaleString('pt-BR')}
                          </td>
                          <td style={{ padding: '0.6rem 0.4rem', textAlign: 'right', color: metricColor(post.comments, maxC) }}>
                            {post.comments}
                          </td>
                          <td style={{ padding: '0.6rem 0.4rem', textAlign: 'right', color: metricColor(post.saves, maxSv) }}>
                            {post.saves}
                          </td>
                          <td style={{ padding: '0.6rem 0.4rem', textAlign: 'right', color: metricColor(post.shares, maxSh) }}>
                            {post.shares}
                          </td>
                          <td style={{ padding: '0.6rem 0.4rem', textAlign: 'right', color: post.plays > 0 ? '#a855f7' : '#444' }}>
                            {post.plays > 0 ? post.plays.toLocaleString('pt-BR') : '—'}
                          </td>
                          <td style={{ padding: '0.6rem 0.4rem', textAlign: 'right', color: post.engagement_rate >= 5 ? '#00ff41' : post.engagement_rate >= 2 ? '#ffeb3b' : '#888' }}>
                            {post.engagement_rate.toFixed(1)}%
                          </td>
                          <td style={{ padding: '0.6rem 0.4rem', textAlign: 'right' }}>
                            <div style={{
                              display: 'inline-block', padding: '0.15rem 0.4rem', borderRadius: '4px',
                              background: isTop ? '#ffd70022' : isBad ? '#ff073a18' : '#333',
                              color: isTop ? '#ffd700' : isBad ? '#ff4444' : '#aaa',
                              fontWeight: 'bold', fontSize: '0.72rem'
                            }}>
                              {post.engagement_score}
                            </div>
                          </td>
                          <td style={{ padding: '0.6rem 0.4rem', textAlign: 'center' }}>
                            {post.permalink ? (
                              <a href={post.permalink} target="_blank" rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                style={{ color: '#3b82f6', textDecoration: 'none', fontSize: '0.8rem' }}>↗</a>
                            ) : '—'}
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr style={{ background: '#12121f' }}>
                            <td colSpan={12} style={{ padding: '1rem 1.5rem' }}>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                <div>
                                  <div style={{ color: '#888', fontSize: '0.65rem', textTransform: 'uppercase', marginBottom: '0.3rem' }}>Legenda completa</div>
                                  <div style={{ color: '#ccc', fontSize: '0.8rem', lineHeight: 1.5, fontStyle: 'italic' }}>
                                    "{post.caption || 'sem legenda'}"
                                  </div>
                                </div>
                                <div>
                                  <div style={{ color: '#888', fontSize: '0.65rem', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Métricas detalhadas</div>
                                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem' }}>
                                    {[
                                      { label: 'Alcance', value: post.reach.toLocaleString('pt-BR'), color: '#ff4444' },
                                      { label: 'Likes', value: post.likes.toLocaleString('pt-BR'), color: '#ff00ff' },
                                      { label: 'Comentários', value: post.comments.toString(), color: '#00ffcc' },
                                      { label: 'Saves', value: post.saves.toString(), color: '#ffd700' },
                                      { label: 'Shares', value: post.shares.toString(), color: '#3b82f6' },
                                      { label: 'Eng. Rate', value: `${post.engagement_rate.toFixed(1)}%`, color: '#00ff41' },
                                    ].map(m => (
                                      <div key={m.label} style={{ background: '#0d0d0d', borderRadius: '4px', padding: '0.5rem', textAlign: 'center' }}>
                                        <div style={{ color: m.color, fontSize: '1.1rem', fontWeight: 'bold' }}>{m.value}</div>
                                        <div style={{ color: '#555', fontSize: '0.6rem', textTransform: 'uppercase' }}>{m.label}</div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })() : (
          <div style={{ color: '#666', fontSize: '0.9rem', textAlign: 'center', padding: '2rem' }}>
            Nenhum post encontrado nos últimos 30 dias
          </div>
        )}
      </div>

      {/* 💰 Tráfego Pago — Todos os Anúncios */}
      <div style={{ background: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '1.5rem', marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <h3 style={{ color: '#ff9500', margin: 0, textTransform: 'uppercase', fontSize: '0.9rem', letterSpacing: '2px' }}>
            💰 Tráfego Pago — Anúncios
          </h3>
          <span style={{ color: '#666', fontSize: '0.7rem' }}>
            {(data.all_ads_table || []).length} anúncios · clique no cabeçalho para ordenar
          </span>
        </div>
        {/* Date filter */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <span style={{ color: '#888', fontSize: '0.75rem' }}>📅 Filtrar período:</span>
          <input
            type="date"
            value={adDateFrom}
            onChange={(e) => setAdDateFrom(e.target.value)}
            style={{
              background: '#111', border: '1px solid #444', borderRadius: '4px', color: '#ddd',
              padding: '0.3rem 0.5rem', fontSize: '0.78rem', cursor: 'pointer',
              colorScheme: 'dark' as any,
            }}
          />
          <span style={{ color: '#555', fontSize: '0.75rem' }}>até</span>
          <input
            type="date"
            value={adDateTo}
            onChange={(e) => setAdDateTo(e.target.value)}
            style={{
              background: '#111', border: '1px solid #444', borderRadius: '4px', color: '#ddd',
              padding: '0.3rem 0.5rem', fontSize: '0.78rem', cursor: 'pointer',
              colorScheme: 'dark' as any,
            }}
          />
          {(adDateFrom || adDateTo) && (
            <button
              onClick={() => { setAdDateFrom(''); setAdDateTo(''); }}
              style={{
                background: '#333', border: '1px solid #555', borderRadius: '4px', color: '#ff9500',
                padding: '0.3rem 0.6rem', fontSize: '0.72rem', cursor: 'pointer',
              }}
            >
              ✕ Limpar
            </button>
          )}
        </div>
        {(data.all_ads_table || []).length > 0 ? (() => {
          // Filter by date range
          const filteredAds = (data.all_ads_table || []).filter(ad => {
            if (adDateFrom && ad.last_date < adDateFrom) return false;
            if (adDateTo && ad.first_date > adDateTo) return false;
            return true;
          });
          const sortedAds = [...filteredAds].sort((a: any, b: any) => {
            const av = a[adSort.field];
            const bv = b[adSort.field];
            if (typeof av === 'string') return adSort.dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
            return adSort.dir === 'asc' ? av - bv : bv - av;
          });
          const adColStyle = (field: string): React.CSSProperties => ({
            cursor: 'pointer', userSelect: 'none' as const, whiteSpace: 'nowrap' as const,
            color: adSort.field === field ? '#ff9500' : '#888',
            fontSize: '0.7rem', textTransform: 'uppercase' as const, letterSpacing: '1px',
            padding: '0.5rem 0.4rem', borderBottom: '1px solid #333',
          });
          const handleAdSort = (field: string) => {
            setAdSort(prev => ({
              field,
              dir: prev.field === field ? (prev.dir === 'desc' ? 'asc' : 'desc') : 'desc'
            }));
          };
          const adSortArrow = (field: string) => adSort.field === field ? (adSort.dir === 'desc' ? ' ▼' : ' ▲') : '';
          const maxSpend = Math.max(...sortedAds.map(a => a.spend), 1);
          const totalSpend = sortedAds.reduce((s, a) => s + a.spend, 0);
          const totalClicks = sortedAds.reduce((s, a) => s + a.clicks, 0);
          const totalReach = sortedAds.reduce((s, a) => s + a.reach, 0);
          const filterActive = adDateFrom || adDateTo;

          return (
            <div style={{ overflowX: 'auto' }}>
              {/* Summary row */}
              <div style={{ display: 'flex', gap: '2rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                {filterActive && <span style={{ color: '#ff9500', fontSize: '0.72rem', background: '#332200', padding: '0.15rem 0.5rem', borderRadius: '4px' }}>🔍 {sortedAds.length} de {(data.all_ads_table || []).length} anúncios</span>}
                <div style={{ fontSize: '0.8rem' }}><span style={{ color: '#888' }}>Total gasto: </span><span style={{ color: '#ff9500', fontWeight: 'bold' }}>R$ {totalSpend.toFixed(2)}</span></div>
                <div style={{ fontSize: '0.8rem' }}><span style={{ color: '#888' }}>Total cliques: </span><span style={{ color: '#00ffcc', fontWeight: 'bold' }}>{totalClicks.toLocaleString('pt-BR')}</span></div>
                <div style={{ fontSize: '0.8rem' }}><span style={{ color: '#888' }}>Alcance total: </span><span style={{ color: '#3b82f6', fontWeight: 'bold' }}>{totalReach.toLocaleString('pt-BR')}</span></div>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead>
                  <tr>
                    <th style={adColStyle('ad_name')} onClick={() => handleAdSort('ad_name')}>Anúncio{adSortArrow('ad_name')}</th>
                    <th style={adColStyle('campaign')} onClick={() => handleAdSort('campaign')}>Campanha{adSortArrow('campaign')}</th>
                    <th style={{ ...adColStyle('spend'), textAlign: 'right' }} onClick={() => handleAdSort('spend')}>Gasto{adSortArrow('spend')}</th>
                    <th style={{ ...adColStyle('reach'), textAlign: 'right' }} onClick={() => handleAdSort('reach')}>Alcance{adSortArrow('reach')}</th>
                    <th style={{ ...adColStyle('clicks'), textAlign: 'right' }} onClick={() => handleAdSort('clicks')}>Cliques{adSortArrow('clicks')}</th>
                    <th style={{ ...adColStyle('cpm'), textAlign: 'right' }} onClick={() => handleAdSort('cpm')}>CPM{adSortArrow('cpm')}</th>
                    <th style={{ ...adColStyle('ctr'), textAlign: 'right' }} onClick={() => handleAdSort('ctr')}>CTR{adSortArrow('ctr')}</th>
                    <th style={{ ...adColStyle('cpc'), textAlign: 'right' }} onClick={() => handleAdSort('cpc')}>CPC{adSortArrow('cpc')}</th>
                    <th style={{ ...adColStyle('active_days'), textAlign: 'right' }} onClick={() => handleAdSort('active_days')}>Dias{adSortArrow('active_days')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedAds.map((ad, idx) => {
                    const spendPct = ad.spend / maxSpend;
                    const isTop = spendPct >= 0.5;
                    const rowBg = isTop ? '#1a1a0f' : (idx % 2 === 0 ? '#1e1e1e' : '#171717');
                    // CPM color: green if low, red if high
                    const cpmColor = ad.cpm <= 5 ? '#00ff41' : ad.cpm <= 10 ? '#ffeb3b' : ad.cpm <= 20 ? '#ff9500' : '#ff4444';
                    // CTR color: green if high
                    const ctrColor = ad.ctr >= 4 ? '#00ff41' : ad.ctr >= 2 ? '#ffeb3b' : ad.ctr >= 1 ? '#ff9500' : '#ff4444';
                    // CPC color: green if low
                    const cpcColor = ad.cpc <= 0.15 ? '#00ff41' : ad.cpc <= 0.50 ? '#ffeb3b' : ad.cpc <= 1.50 ? '#ff9500' : '#ff4444';

                    // Detect type from ad name
                    const adNameLower = ad.ad_name.toLowerCase();
                    const isBoost = ad.source === 'boost';
                    const adIcon = isBoost ? '🚀' : adNameLower.includes('reels') || adNameLower.includes('reel') ? '🎬' : adNameLower.includes('video') || adNameLower.includes('vídeo') ? '📹' : adNameLower.includes('carros') ? '📑' : adNameLower.includes('feed') ? '📷' : adNameLower.includes('depoimento') ? '🗣️' : adNameLower.includes('story') ? '📱' : '📢';

                    return (
                      <tr key={idx}
                        style={{ borderBottom: '1px solid #222', background: rowBg, transition: 'background 0.15s' }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = '#252525'; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = rowBg; }}
                      >
                        <td style={{ padding: '0.6rem 0.4rem', color: '#ddd', maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {isTop && <span style={{ color: '#ffd700', marginRight: '4px' }}>🔥</span>}
                          {adIcon} {ad.ad_name}
                          {isBoost && <span style={{ marginLeft: '6px', fontSize: '0.6rem', background: '#2563eb', color: '#fff', padding: '1px 5px', borderRadius: '3px', verticalAlign: 'middle' }}>BOOST</span>}
                          {ad.preview_link && (
                            <a href={ad.preview_link} target="_blank" rel="noopener noreferrer"
                              style={{ marginLeft: '6px', color: '#3b82f6', textDecoration: 'none', fontSize: '0.75rem' }}
                              title="Ver anúncio"
                            >🔗</a>
                          )}
                        </td>
                        <td style={{ padding: '0.6rem 0.4rem', color: '#888', fontSize: '0.72rem', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {ad.campaign}
                        </td>
                        <td style={{ padding: '0.6rem 0.4rem', textAlign: 'right', fontWeight: 'bold', color: '#ff9500' }}>
                          R$ {ad.spend.toFixed(2)}
                          <div style={{ height: '2px', background: `linear-gradient(to right, #ff9500 ${spendPct * 100}%, transparent ${spendPct * 100}%)`, marginTop: '3px', borderRadius: '1px' }} />
                        </td>
                        <td style={{ padding: '0.6rem 0.4rem', textAlign: 'right', color: '#3b82f6' }}>
                          {ad.reach.toLocaleString('pt-BR')}
                        </td>
                        <td style={{ padding: '0.6rem 0.4rem', textAlign: 'right', color: '#00ffcc', fontWeight: 'bold' }}>
                          {ad.clicks.toLocaleString('pt-BR')}
                        </td>
                        <td style={{ padding: '0.6rem 0.4rem', textAlign: 'right', color: cpmColor, fontWeight: 'bold' }}>
                          R$ {ad.cpm.toFixed(2)}
                        </td>
                        <td style={{ padding: '0.6rem 0.4rem', textAlign: 'right', color: ctrColor, fontWeight: 'bold' }}>
                          {ad.ctr.toFixed(2)}%
                        </td>
                        <td style={{ padding: '0.6rem 0.4rem', textAlign: 'right', color: cpcColor, fontWeight: 'bold' }}>
                          R$ {ad.cpc.toFixed(2)}
                        </td>
                        <td style={{ padding: '0.6rem 0.4rem', textAlign: 'right', color: '#888' }}>
                          {ad.active_days}d
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })() : (
          <div style={{ color: '#666', fontSize: '0.9rem', textAlign: 'center', padding: '2rem' }}>
            Nenhum anúncio encontrado
          </div>
        )}
      </div>

      {/* Footer Stats */}
      <div style={{ background: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '1rem', display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: '1rem' }}>
        <div style={{ textAlign: 'center' }}>
          <span style={{ color: '#888', fontSize: '0.75rem', display: 'block' }}>CPM Médio (7d)</span>
          <span style={{ color: '#00ff41', fontWeight: 'bold' }}>R$ {(data.ads.cpm_avg_7d || 0).toFixed(2)}</span>
        </div>
        <div style={{ textAlign: 'center' }}>
          <span style={{ color: '#888', fontSize: '0.75rem', display: 'block' }}>Δ CPM vs 7d</span>
          <span style={{ color: (data.ads.cpm_delta_pct || 0) > 0 ? '#ff4444' : '#00ff41', fontWeight: 'bold' }}>
            {(data.ads.cpm_delta_pct || 0) > 0 ? '+' : ''}{(data.ads.cpm_delta_pct || 0).toFixed(1)}%
          </span>
        </div>
        <div style={{ textAlign: 'center' }}>
          <span style={{ color: '#888', fontSize: '0.75rem', display: 'block' }}>Engagement Rate</span>
          <span style={{ color: '#00ff41', fontWeight: 'bold' }}>{data.instagram.engagement_rate || 0}%</span>
        </div>
        <div style={{ textAlign: 'center' }}>
          <span style={{ color: '#888', fontSize: '0.75rem', display: 'block' }}>Seguidores</span>
          <span style={{ color: '#8b5cf6', fontWeight: 'bold' }}>{(data.instagram.followers_count || 0).toLocaleString('pt-BR')}</span>
        </div>
        <div style={{ textAlign: 'center' }}>
          <span style={{ color: '#888', fontSize: '0.75rem', display: 'block' }}>Δ Seguidores/dia</span>
          <span style={{ color: (data.instagram.follower_velocity || 0) >= 0 ? '#22c55e' : '#ef4444', fontWeight: 'bold' }}>
            {(data.instagram.follower_velocity || 0) >= 0 ? '+' : ''}{(data.instagram.follower_velocity || 0).toFixed(1)}
          </span>
        </div>
        <div style={{ textAlign: 'center' }}>
          <span style={{ color: '#888', fontSize: '0.75rem', display: 'block' }}>Stories 7d</span>
          <span style={{ color: '#3b82f6', fontWeight: 'bold' }}>{data.instagram.stories_total_7d || 0}</span>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </div>
  );
};

export default WarRoomDashboard;
